import { Check, ChevronDown, type LucideIcon } from "lucide-react";
import { useEffect, useId, useState, type ReactNode } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { WhatsAppIcon } from "@/shared/components/notifications/WhatsAppIcon";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

/**
 * `coming_soon` is NOT a disabled `available` card. A disabled control reads as "you may not do this"; this
 * one means "the product cannot do this yet", so the switch is replaced by that sentence instead of being
 * greyed out.
 */
export type NotificationTypeAvailability = "available" | "coming_soon";

export interface NotificationTypeCardProps {
  icon: LucideIcon;
  /** Brand-ish accent painted behind the icon. A raw colour, because each event owns its own hue. */
  iconBg: string;
  name: string;
  desc: string;
  enabled: boolean;
  /** Transient lock — a save in flight, a missing organization. Orthogonal to `availability`. */
  disabled?: boolean;
  availability?: NotificationTypeAvailability;
  /** Extra chip next to the name, e.g. the opt-in warning on high-volume events. */
  optInTag?: string;
  /** The message preview, shown when the row is expanded. */
  bubble?: ReactNode;
  /** Inline configuration the event needs before it can be switched on (the balance-low threshold). */
  belowBody?: ReactNode;
  onToggle?: (next: boolean) => void;
  /** `false` means the sample did not go out; the host already said why. */
  onSendTest?: () => Promise<boolean | void> | boolean | void;
  /** The page's test number, already formatted. Empty disables the test send. */
  testRecipient?: string;
}

export function NotificationTypeCard({
  icon: Icon,
  iconBg,
  name,
  desc,
  enabled,
  disabled,
  availability = "available",
  optInTag,
  bubble,
  belowBody,
  onToggle,
  onSendTest,
  testRecipient,
}: NotificationTypeCardProps) {
  const { t } = useTranslations("notificationPreferences");
  const panelId = useId();
  const [expanded, setExpanded] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const isComingSoon = availability === "coming_soon";
  // A pending event is never on, whatever the caller passes — there is no backend flag behind it to be on.
  const isOn = enabled && !isComingSoon;
  const canTest = Boolean(onSendTest) && !isComingSoon;

  useEffect(() => {
    if (!testSent) return;
    const timer = setTimeout(() => setTestSent(false), 3000);
    return () => clearTimeout(timer);
  }, [testSent]);

  const sendTest = async () => {
    if (!onSendTest || isSending || !testRecipient) return;
    setIsSending(true);
    try {
      if ((await onSendTest()) !== false) setTestSent(true);
    } catch {
      // The toast is the host's job — it owns the service call and its error message.
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className={cn(
        "overflow-hidden rounded-md border transition-colors",
        isOn ? "border-primary/30 bg-card" : "border-border bg-muted/30",
      )}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div
          className="mt-0.5 grid h-[34px] w-[34px] shrink-0 place-items-center rounded-md border text-white"
          style={{
            background: isOn ? iconBg : undefined,
            borderColor: isOn ? iconBg : undefined,
            opacity: isOn ? 1 : 0.5,
          }}
        >
          <Icon size={16} className={isOn ? undefined : "text-muted-foreground"} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-foreground">{name}</span>
            {optInTag && <ColocaBadge variant="secondary">{optInTag}</ColocaBadge>}
          </div>
          <div className="mt-0.5 text-[12px] text-muted-foreground">{desc}</div>
          {bubble && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={panelId}
              className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline"
            >
              {expanded ? t("message_hide") : t("message_show")}
              <ChevronDown size={13} className={cn("transition-transform", expanded && "rotate-180")} />
            </button>
          )}
        </div>

        {isComingSoon ? (
          <span className="shrink-0 pt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {t("status_coming_soon")}
          </span>
        ) : (
          <div className="mt-1 shrink-0">
            <ColocaSwitch
              checked={isOn}
              onCheckedChange={(v) => onToggle?.(v)}
              disabled={disabled}
              aria-label={name}
            />
          </div>
        )}
      </div>

      {belowBody && <div className="border-t border-border bg-muted/20 px-4 py-3">{belowBody}</div>}

      {bubble && expanded && (
        <div id={panelId} className="border-t border-border">
          {bubble}
          {canTest && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border px-4 py-3">
              <ColocaButton
                type="button"
                variant="secondary"
                size="sm"
                loading={isSending}
                disabled={!testRecipient || testSent}
                onClick={sendTest}
              >
                <span className="inline-flex items-center gap-1.5">
                  {testSent ? <Check size={13} /> : <WhatsAppIcon size={13} />}
                  {testSent ? t("send_test_sent") : t("send_test_button")}
                </span>
              </ColocaButton>
              <span className="text-[11.5px] text-muted-foreground">
                {testRecipient ? t("send_test_to", { phone: testRecipient }) : t("send_test_needs_phone")}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
